import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, Crop } from 'lucide-react';
import { formatDuration } from '@/utils/helpers';
import { NormalizedRect } from '@/types';

interface VideoPlayerProps {
  src?: string;
  blobUrl?: string;
  poster?: string;
  cropPreview?: NormalizedRect | null;
  className?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  blobUrl,
  poster,
  cropPreview,
  className = ''
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isCroppedMode, setIsCroppedMode] = useState(false);

  const videoSource = blobUrl || src;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onLoadedMetadata = () => setDuration(video.duration || 0);
    const onEnded = () => setIsPlaying(false);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
    if (videoRef.current) {
      videoRef.current.volume = val;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div className={`relative group bg-black rounded-2xl overflow-hidden border border-brand-border shadow-elevated ${className}`}>
      {/* Video Element */}
      <div className="relative w-full aspect-video overflow-hidden flex items-center justify-center">
        {videoSource ? (
          <div
            className="w-full h-full relative transition-transform duration-300"
            style={
              isCroppedMode && cropPreview
                ? {
                    transform: `scale(${1 / cropPreview.width}) translate(-${cropPreview.x * 100}%, -${cropPreview.y * 100}%)`,
                    transformOrigin: 'top left'
                  }
                : undefined
            }
          >
            <video
              ref={videoRef}
              src={videoSource}
              poster={poster}
              playsInline
              onClick={togglePlay}
              className="w-full h-full object-contain cursor-pointer"
            />
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-brand-surface/40 text-brand-muted">
            <p className="text-sm">Video Source Not Loaded</p>
          </div>
        )}
      </div>

      {/* Floating Controls Overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 opacity-95 group-hover:opacity-100 transition-opacity">
        {/* Scrubber Bar */}
        <div className="flex items-center gap-2 mb-2">
          <input
            type="range"
            min={0}
            max={duration || 100}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-brand-accent hover:accent-brand-secondary transition-all"
          />
        </div>

        {/* Buttons and Time */}
        <div className="flex items-center justify-between text-white text-xs">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
            </button>

            <div className="flex items-center gap-1.5 font-mono text-[11px] text-white/80">
              <span>{formatDuration(currentTime)}</span>
              <span>/</span>
              <span>{formatDuration(duration)}</span>
            </div>

            {/* Volume control */}
            <div className="flex items-center gap-1.5 ml-2">
              <button onClick={toggleMute} className="text-white/70 hover:text-white">
                {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-16 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {cropPreview && (
              <button
                onClick={() => setIsCroppedMode(!isCroppedMode)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  isCroppedMode
                    ? 'bg-brand-secondary text-brand-bg border-brand-secondary'
                    : 'bg-white/10 hover:bg-white/20 text-white border-white/20'
                }`}
                title="Toggle cropped participant viewport preview"
              >
                <Crop className="w-3.5 h-3.5" />
                <span>{isCroppedMode ? 'Cropped View' : 'Full Screen'}</span>
              </button>
            )}

            <button
              onClick={handleFullscreen}
              className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-colors"
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
